/**
 * Centered headline + description with icon feature cards underneath.
 *
 * Aliases: icon highlights, trust section, feature pillars, value props
 */

import type { ReactNode } from "react";

import { cn } from "~/lib/cn";

import { NamedIcon } from "./named-icon";

type IconItem = {
  readonly iconName: string;
  readonly label: string;
  readonly description?: string;
};

type CenteredIconSectionProps = {
  readonly preHeadline?: string;
  readonly headline?: string;
  readonly description?: string;
  readonly items?: IconItem[];
  readonly tone?: "purple" | "slate" | "plain";
  readonly columns?: 2 | 3 | 4;
  readonly className?: string;
};

const toneStyles = {
  purple: "bg-purple-50/60",
  slate: "bg-gray-50/60",
  plain: "",
} as const;

const iconToneStyles = {
  purple: "bg-purple-100 text-purple-700",
  slate: "bg-gray-100 text-gray-700",
  plain: "bg-gray-100 text-gray-700",
} as const;

const columnStyles = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
} as const;

export function CenteredIconSection({
  preHeadline,
  headline,
  description,
  items = [],
  tone = "purple",
  columns = 3,
  className,
}: CenteredIconSectionProps): ReactNode {
  return (
    <section className={cn("py-16 sm:py-20", toneStyles[tone], className)}>
      <div className="mx-auto max-w-screen-xl px-4">
        {(preHeadline || headline || description) && (
          <div className="mx-auto max-w-2xl text-center">
            {preHeadline && (
              <p className="mb-1 text-sm font-medium tracking-wide text-purple-700 uppercase">
                {preHeadline}
              </p>
            )}
            {headline && (
              <h2 className="text-text-primary mb-4 font-heading text-3xl font-medium tracking-tight sm:text-4xl">
                {headline}
              </h2>
            )}
            {description && (
              <p className="text-text-secondary text-lg leading-relaxed">
                {description}
              </p>
            )}
          </div>
        )}

        {items.length > 0 && (
          <div
            className={cn(
              "mx-auto mt-12 grid max-w-4xl gap-8",
              columnStyles[columns],
            )}
          >
            {items.map(({ iconName, label, description: desc }) => (
              <div key={label} className="text-center">
                <div
                  className={cn(
                    "mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full",
                    iconToneStyles[tone],
                  )}
                >
                  <NamedIcon name={iconName} className="h-6 w-6" />
                </div>
                <p className="font-heading text-base font-semibold text-gray-900">
                  {label}
                </p>
                {desc && (
                  <p className="text-text-secondary mt-1 text-sm leading-relaxed">
                    {desc}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
